import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { api, toastErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/common";

// "Inizia turno" / "Modifica profili": profiles worked in this shift (the waiting bot follows them). Remembers the last choice.
export function ShiftAccountsDialog({ open, onClose, onConfirm, current, pending, title = "Su quali profili lavori in questo turno?" }) {
  const [opts, setOpts] = useState(null);
  const [sel, setSel] = useState([]);
  const touched = useRef(false);
  useEffect(() => {
    if (!open) return;
    let live = true;  // a late or duplicate answer never overwrites what the user already ticked
    touched.current = false;
    setOpts(null);
    api.get("/work/start-options").then((r) => {
      if (!live) return;
      const ok = new Set(r.data.accounts.map((a) => a.id));
      setOpts(r.data.accounts);
      if (!touched.current) setSel((current || r.data.last || []).filter((i) => ok.has(i)));  // remembered profiles count as selected
    }).catch((e) => live && toastErr(e));
    return () => { live = false; };
  }, [open]); // eslint-disable-line
  const flip = (id) => { touched.current = true; setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])); };
  const none = opts && opts.length === 0;
  const blocked = pending || !opts || (!none && sel.length === 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#12141D] border-[#232738] max-w-md max-h-[90vh] flex flex-col" data-testid="shift-accounts-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {!opts ? <div className="text-sm text-slate-500">Caricamento…</div> : (
          <>
            {!none && <div className="text-[11px] text-slate-400" data-testid="shift-accounts-count">Selezionati {sel.length} di {opts.length}</div>}
            <div className="grid gap-1.5 min-h-0 flex-1 max-h-[55vh] overflow-y-auto overscroll-contain thin-scroll pr-1" data-testid="shift-accounts-list">
              {opts.map((a) => (
                <button key={a.id} type="button" onClick={() => flip(a.id)} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm shrink-0 ${sel.includes(a.id) ? "border-violet-500 bg-violet-600/20" : "border-[#232738] hover:bg-white/5"}`} data-testid={`shift-account-${a.username || a.id}`}>
                  <Avatar name={a.username || a.label || ""} url={a.avatar_url} size={26} /><span className="font-semibold truncate">@{a.username || a.label}</span>
                  {sel.includes(a.id) && <Check className="ml-auto h-4 w-4 shrink-0 text-violet-300" />}
                </button>))}
              {none && <div className="text-sm text-slate-500">Nessun profilo visibile per il tuo account: il turno parte senza profili.</div>}
            </div>
          </>)}
        <div className="text-[11px] text-slate-500">Ricorda i profili dell'ultima volta. Si possono cambiare durante il turno.</div>
        <Button disabled={blocked} onClick={() => !blocked && onConfirm([...sel])} className="bg-emerald-600 hover:bg-emerald-500 shrink-0" data-testid="shift-accounts-confirm">
          {current ? "Salva profili" : `Inizia turno${sel.length ? ` su ${sel.length} ${sel.length === 1 ? "profilo" : "profili"}` : ""}`}</Button>
      </DialogContent>
    </Dialog>
  );
}
